import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import TeamCalendar from '../components/TeamCalendar';
import TrainingAttendance from '../components/TrainingAttendance';
import ActivityFeed from '../components/ActivityFeed';
import PositionSelectionModal from '../components/PositionSelectionModal';
import { socialAPI, teamsAPI } from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showPositionModal, setShowPositionModal] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);

  const handleEventChange = () => {
    setRefreshKey(prev => prev + 1);
  };

  React.useEffect(() => {
    const checkPositionStatus = async () => {
      // Only for players with an approved team
      if (user?.role === 'player' && user?.team_id) {
        // Check local storage skip flag first
        const skipped = localStorage.getItem(`position_prompt_skipped_${user.username}`);
        if (skipped) return;

        try {
          // Get my team member profile
          const membersResponse = await teamsAPI.getMembers(user.team_id);
          const myMember = membersResponse.data.find(m => m.user_id === user.id);

          // If no positions set in TEAM MEMBER profile, show modal
          if (myMember && (!myMember.positions || myMember.positions.length === 0)) {
            setShowPositionModal(true);
          }
        } catch (error) {
          console.error("Failed to check profile positions:", error);
        }
      }
    };

    if (user) {
      checkPositionStatus();
    }
  }, [user]);

  const handlePositionComplete = () => {
    setShowPositionModal(false);
  };

  return (
    <div className="min-h-screen bg-[#121212]">
      <div className="px-4 lg:px-8 pb-8 pt-4 md:pt-6">
        {/* Dashboard Header with Team Logo */}
        <div className="mb-6 flex items-center gap-3 md:gap-4">
          {user?.team_logo && (
            <img
              src={user.team_logo}
              alt="Team Logo"
              className="w-12 h-12 md:w-16 md:h-16 rounded-lg object-cover border-2 border-gray-700"
            />
          )}
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-white mb-1 md:mb-2 leading-tight">{t('dashboard.title')}</h1>
            <p className="text-xs md:text-base text-gray-400">{t('dashboard.subtitle')}</p>
          </div>
        </div>

        {/* Team Calendar - Full Width */}
        {user?.team_id && (
          <div className="mb-8">
            <TeamCalendar onEventChange={handleEventChange} />
          </div>
        )}

        {/* Two Column Layout: Training Attendance | Activity Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Training Attendance */}
          <div className="min-h-[400px]">
            <TrainingAttendance refreshKey={refreshKey} />
          </div>

          {/* Right: Activity Feed */}
          <div className="min-h-[400px]">
            <ActivityFeed />
          </div>
        </div>
      </div>

      <PositionSelectionModal
        open={showPositionModal}
        onOpenChange={setShowPositionModal}
        onComplete={handlePositionComplete}
        username={user?.username}
        teamId={user?.team_id}
      />
    </div>
  );
};

export default Dashboard;
